import { Component, type ReactNode } from 'react'
import { AlertTriangleIcon, RefreshCwIcon, HomeIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to console in dev; in production this would go to Sentry
    console.error('[ErrorBoundary]', error, errorInfo.componentStack)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="flex min-h-[400px] items-center justify-center p-8">
          <div className="flex max-w-md flex-col items-center gap-4 text-center">
            <div className="flex size-16 items-center justify-center rounded-2xl bg-destructive/10">
              <AlertTriangleIcon className="size-8 text-destructive" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Something went wrong</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                An unexpected error occurred while rendering this page.
                You can try reloading to recover.
              </p>
            </div>
            {this.state.error && (
              <details className="w-full rounded-lg border border-border bg-muted/50 p-3 text-left">
                <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                  Error details
                </summary>
                <pre className="mt-2 max-h-32 overflow-auto text-[11px] text-destructive whitespace-pre-wrap">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              <Button onClick={this.handleRetry} className="gap-2">
                <RefreshCwIcon className="size-4" />
                Try again
              </Button>
              <Button variant="outline" asChild className="gap-2">
                <a href="/">
                  <HomeIcon className="size-4" />
                  Go home
                </a>
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
